import Component from '@glimmer/component';
import { service } from '@ember/service';
import { getPromiseState } from '@warp-drive/core/reactive';
import { t, type IntlService } from 'ember-intl';
import { PhSun } from 'ember-phosphor-icons';
import AlertBanner from 'tio-ui/components/alert-banner';
import { Section, VStack } from 'tio-ui/components/layout';
import AttentionPanel from 'tio-employee/components/dashboard-kit/attention-panel';
import DashboardHero from 'tio-employee/components/dashboard-kit/hero';
import TaskCard from 'tio-employee/components/dashboard-kit/task-card';
import WidgetFrame from 'tio-employee/components/partner-dashboard/widget-frame';
import { greetingKey } from 'tio-employee/utils/greeting';
import type { DashboardSection } from 'tio-employee/utils/partner-dashboard/registry';
import type { PartnerDashboardModel } from 'tio-employee/routes/authenticated/admin/index';
import type SessionContextService from 'tio-employee/services/session-context';

interface PartnerDashboardPageSignature {
  Args: {
    /** The route's persona context, each visible widget with its in-flight data, and the attention list. */
    model: PartnerDashboardModel;
  };
}

/**
 * Role-based partner dashboard on the Figma Employee Home layout: Summary
 * hero, Needs Your Attention (one merged, deadline-sorted list that always
 * renders), Pertinent info, and Exploration.
 */
export default class PartnerDashboardPage extends Component<PartnerDashboardPageSignature> {
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  get greeting() {
    const base = this.intl.t(greetingKey(new Date().getHours()));
    const name = this.sessionContext.currentEmployee?.firstName;
    return name ? `${base}, ${name}` : base;
  }

  get heroWidgets() {
    return this.#widgetsIn('hero');
  }

  get attention() {
    return getPromiseState(this.args.model.attention);
  }

  /** The list rejected outright, or every visible source failed (none loaded, even empty). */
  get attentionUnavailable() {
    if (this.attention.isError) return true;
    const result = this.attention.result;
    return Boolean(result && result.loadedSources === 0 && result.failedSources.length > 0);
  }

  /** Pertinent info, then Exploration, each under its own heading; a section with no widgets is omitted. */
  get lowerSections() {
    return [
      { section: 'pertinent' as const, titleKey: 'partner_dashboard.pertinent_title' },
      { section: 'explore' as const, titleKey: 'partner_dashboard.explore_title' },
    ]
      .map((group) => ({ ...group, widgets: this.#widgetsIn(group.section) }))
      .filter(({ widgets }) => widgets.length > 0);
  }

  #widgetsIn(section: DashboardSection) {
    return this.args.model.widgets.filter((widget) => widget.definition.section === section);
  }

  <template>
    <VStack class="gap-8" data-test-partner-dashboard>
      <DashboardHero
        @greeting={{this.greeting}}
        @icon={{PhSun}}
        @title={{t "partner_dashboard.title"}}
      >
        <:stats>
          {{#if this.heroWidgets.length}}
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
              {{#each this.heroWidgets as |widget|}}
                <WidgetFrame @widget={{widget}} />
              {{/each}}
            </div>
          {{/if}}
        </:stats>
      </DashboardHero>

      {{#if this.attention.isPending}}
        <section
          class="tio-col rounded-2xl tio-bg-sunken tio-fg-primary p-4"
          role="status"
          aria-busy="true"
          data-test-partner-dashboard-attention-loading
        >
          <h2>{{t "partner_dashboard.needs_attention"}}</h2>
          <span class="sr-only">{{t "common.loading"}}</span>
          <div class="h-4 w-1/3 tio-bg-skeleton rounded-full"></div>
          <div class="h-4 tio-bg-skeleton rounded-full"></div>
        </section>
      {{else if this.attentionUnavailable}}
        {{! Nothing loaded, so the panel's "all caught up" empty state would be false reassurance. }}
        <section
          class="tio-col rounded-2xl tio-bg-sunken tio-fg-primary p-4"
          data-test-partner-dashboard-attention-error
        >
          <h2>{{t "partner_dashboard.needs_attention"}}</h2>
          <AlertBanner @intent="danger" @title={{t "partner_dashboard.widget_error"}} />
        </section>
      {{else if this.attention.result}}
        <div class="tio-col" data-test-partner-dashboard-attention>
          <AttentionPanel
            @title={{t "partner_dashboard.needs_attention"}}
            @count={{this.attention.result.items.length}}
            @emptyTitle={{t "partner_dashboard.needs_attention_empty_title"}}
            @emptyBody={{t "partner_dashboard.needs_attention_empty_body"}}
          >
            {{#each this.attention.result.items key="id" as |item|}}
              {{! Two branches because TaskCard's Args only accept an action link when both label and route are set. }}
              {{#if item.action}}
                <TaskCard
                  @title={{item.title}}
                  @description={{item.description}}
                  @icon={{item.icon}}
                  @urgency={{item.urgency}}
                  @dueLabel={{item.dueLabel}}
                  @actionLabel={{item.action.label}}
                  @actionRoute={{item.action.route}}
                  @actionModel={{item.action.model}}
                  @actionQuery={{item.action.query}}
                  class={{if item.pii "pii"}}
                  data-test-partner-dashboard-attention-item={{item.id}}
                />
              {{else}}
                <TaskCard
                  @title={{item.title}}
                  @description={{item.description}}
                  @icon={{item.icon}}
                  @urgency={{item.urgency}}
                  @dueLabel={{item.dueLabel}}
                  class={{if item.pii "pii"}}
                  data-test-partner-dashboard-attention-item={{item.id}}
                />
              {{/if}}
            {{/each}}
          </AttentionPanel>
          {{#if this.attention.result.failedSources.length}}
            <AlertBanner
              @intent="warning"
              @title={{t "partner_dashboard.attention_partial"}}
              data-test-partner-dashboard-attention-partial
            />
          {{/if}}
        </div>
      {{/if}}

      {{#each this.lowerSections as |group|}}
        <Section data-test-partner-dashboard-section={{group.section}}>
          <:header>{{t group.titleKey}}</:header>
          <:body>
            <div class="tio-col-extra-loose">
              {{#each group.widgets as |widget|}}
                <WidgetFrame @widget={{widget}} />
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/each}}
    </VStack>
  </template>
}
